import type { AgeBand, AstroSummary, ConnectionIntent, LifeStage } from '@astrally/shared';
import { computeAshtakoota } from '@astrally/astro-engine';
import type { MatchableProfile } from './profile.js';

/** All dimension scorers return 0..1. Deterministic — no randomness, no LLM. */

export function intentionFit(a: MatchableProfile, b: MatchableProfile): number {
  const shared = a.intents.filter((i) => b.intents.includes(i));
  if (shared.length === 0) return 0;
  const denom = Math.max(1, Math.min(a.intents.length, b.intents.length));
  return Math.min(1, shared.length / denom);
}

export function communicationFit(a: MatchableProfile, b: MatchableProfile): number {
  const pairs: [number, number][] = [
    [a.dimensions.communicationDirectness, b.dimensions.communicationDirectness],
    [a.dimensions.conversationDepth, b.dimensions.conversationDepth],
    [a.dimensions.replyConsistency, b.dimensions.replyConsistency],
    [a.dimensions.socialEnergy, b.dimensions.socialEnergy],
  ];
  const meanDiff = pairs.reduce((s, [x, y]) => s + Math.abs(x - y), 0) / pairs.length;
  return 1 - meanDiff;
}

export function sharedInterests(a: MatchableProfile, b: MatchableProfile): {
  score: number;
  shared: string[];
} {
  const setB = new Set(b.interests.map((i) => i.toLowerCase()));
  const shared = a.interests.filter((i) => setB.has(i.toLowerCase()));
  const union = new Set([...a.interests, ...b.interests].map((i) => i.toLowerCase())).size;
  const jaccard = union === 0 ? 0 : shared.length / union;
  const coverage = Math.min(1, shared.length / 4);
  return { score: 0.55 * coverage + 0.45 * jaccard, shared };
}

const AGE_ORDER: AgeBand[] = ['18-24', '25-34', '35-44', '45-54', '55+'];
const STAGE_ORDER: LifeStage[] = [
  'student',
  'early-career',
  'establishing',
  'established',
  'transitioning',
];

export function lifeStageFit(a: MatchableProfile, b: MatchableProfile): number {
  const ageDist = Math.abs(AGE_ORDER.indexOf(a.ageBand) - AGE_ORDER.indexOf(b.ageBand));
  const ageScore = [1, 0.75, 0.4, 0.2, 0.1][Math.min(ageDist, 4)];
  let stageScore = 0.5;
  if (a.lifeStage && b.lifeStage) {
    const d = Math.abs(STAGE_ORDER.indexOf(a.lifeStage) - STAGE_ORDER.indexOf(b.lifeStage));
    stageScore = [1, 0.65, 0.35, 0.2, 0.2][Math.min(d, 4)];
  }
  return 0.65 * ageScore + 0.35 * stageScore;
}

export function geoPracticality(a: MatchableProfile, b: MatchableProfile): number {
  if (a.city && b.city && a.city.toLowerCase() === b.city.toLowerCase()) return 1;
  if (a.region === b.region) return 0.8;
  const localFocused =
    a.intents.includes('local-activities') || b.intents.includes('local-activities');
  return localFocused ? 0.2 : 0.45;
}

/**
 * Jyotish compatibility from synced astro summaries.
 * Uses ashtakoota when Moon data exists on both sides; averages across
 * possibilities when a birth-time window leaves multiple Moon candidates.
 * Neutral 0.5 when data is missing — absence of data is never a penalty.
 */
export function jyotishCompat(a?: AstroSummary, b?: AstroSummary): number {
  if (!a || !b) return 0.5;
  if (
    a.moonSiderealSigns.length === 0 ||
    b.moonSiderealSigns.length === 0 ||
    a.moonNakshatras.length === 0 ||
    b.moonNakshatras.length === 0
  ) {
    return 0.5;
  }
  const combosA = a.moonSiderealSigns.slice(0, 3);
  const combosB = b.moonSiderealSigns.slice(0, 3);
  let total = 0;
  let count = 0;
  for (let i = 0; i < combosA.length; i++) {
    for (let j = 0; j < combosB.length; j++) {
      const r = computeAshtakoota(
        { rashi: combosA[i], nakshatra: a.moonNakshatras[Math.min(i, a.moonNakshatras.length - 1)] },
        { rashi: combosB[j], nakshatra: b.moonNakshatras[Math.min(j, b.moonNakshatras.length - 1)] },
      );
      total += r.total / r.max;
      count++;
    }
  }
  let score = count > 0 ? total / count : 0.5;
  // Classical mangal-dosha mismatch is a mild adjustment, never an exclusion.
  if ((a.mangalDosha === 'yes') !== (b.mangalDosha === 'yes')) {
    if (a.mangalDosha !== 'unknown' && b.mangalDosha !== 'unknown') score -= 0.08;
  }
  return Math.min(1, Math.max(0, score));
}

type Element = 0 | 1 | 2 | 3; // fire, earth, air, water
const elementOf = (sign: number): Element => (sign % 4) as Element;

function elementHarmony(a: number, b: number): number {
  const ea = elementOf(a);
  const eb = elementOf(b);
  if (ea === eb) return 1;
  const complementary =
    (ea === 0 && eb === 2) || (ea === 2 && eb === 0) || (ea === 1 && eb === 3) || (ea === 3 && eb === 1);
  return complementary ? 0.8 : 0.4;
}

/**
 * Western compatibility from sign-level data (degrees never leave the
 * device, so this is deliberately coarse; full synastry runs on-device).
 */
export function westernCompat(
  a: AstroSummary | undefined,
  b: AstroSummary | undefined,
  sharedIntents: ConnectionIntent[],
): number {
  if (!a || !b) return 0.5;
  const parts: number[] = [elementHarmony(a.sunTropicalSign, b.sunTropicalSign)];
  const moonA = a.moonSiderealSigns[0];
  const moonB = b.moonSiderealSigns[0];
  if (moonA !== undefined && moonB !== undefined) {
    parts.push(elementHarmony(moonA, moonB));
  }
  if (sharedIntents.includes('romance')) {
    parts.push(elementHarmony(a.venusTropicalSign, b.marsTropicalSign));
    parts.push(elementHarmony(b.venusTropicalSign, a.marsTropicalSign));
  }
  return parts.reduce((s, x) => s + x, 0) / parts.length;
}

export function reliabilityFit(a: MatchableProfile, b: MatchableProfile): number {
  const rel = Math.sqrt(a.dimensions.reliability * b.dimensions.reliability);
  const trust = Math.min(a.trustLevel, b.trustLevel);
  return 0.7 * rel + 0.3 * trust;
}
